// The loading rule for the office app (docs/design/states-pass.md), shared by Books, Settings,
// Company and Agents.

import { useEffect, useState } from 'react';
import './loading.css';

/**
 * The loading rule (docs/design/states-pass.md): still placeholders the shape of what comes (blank
 * table rows or blank stones, never a spinner, a "0" or "Loading…"), on a time ladder: nothing for
 * 300 ms, then the placeholders, after 3 s one line naming what is coming, after 10 s the error with
 * Try again. `what` names the thing ("the aging"); `retry` re-reads it.
 */
export function Loading({
  error,
  what = 'this',
  kind = 'stones',
  cols,
  retry,
}: {
  error: string | null;
  what?: string;
  kind?: 'stones' | 'table';
  /** The table's real header row, shown at once. */
  cols?: string[];
  retry?: () => void;
}) {
  const [phase, setPhase] = useState<'wait' | 'shapes' | 'slow' | 'late'>('wait');
  const [round, setRound] = useState(0);
  useEffect(() => {
    const t = [
      setTimeout(() => setPhase((p) => (p === 'wait' ? 'shapes' : p)), 300),
      setTimeout(() => setPhase('slow'), 3000),
      setTimeout(() => setPhase('late'), 10000),
    ];
    return () => t.forEach(clearTimeout);
  }, [round]);
  const again = () => {
    setPhase('shapes');
    setRound((r) => r + 1);
    retry?.();
  };
  const failed =
    (round > 0 && phase !== 'late' ? null : error) ?? (phase === 'late' ? `Couldn’t load ${what}.` : null);
  const shapes =
    kind === 'table' ? (
      <div className="table-wrap">
        <table className="grid-table">
          {cols && (
            <thead>
              <tr>
                {cols.map((c) => (
                  <th key={c}>{c}</th>
                ))}
              </tr>
            </thead>
          )}
          <tbody aria-hidden="true">
            {[0, 1, 2, 3, 4].map((i) => (
              <tr key={i} className="ph-row">
                {(cols ?? ['']).map((c) => (
                  <td key={c} />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : (
      <div className="ph-stones" aria-hidden="true">
        <div className="ph-stone" />
        <div className="ph-stone" />
        <div className="ph-stone" />
      </div>
    );
  return (
    <div className={`loading${phase === 'wait' && !failed ? ' wait' : ''}`} aria-busy={!failed}>
      <p className={failed ? 'error' : 'sayline'} aria-live="polite">
        {failed ? (
          <>
            {failed}{' '}
            {retry && (
              <button className="btn quiet" onClick={again}>
                Try again
              </button>
            )}
          </>
        ) : phase === 'slow' ? (
          `Getting ${what}…`
        ) : null}
      </p>
      {!failed && shapes}
    </div>
  );
}
